/**
 * A U-100 syringe marked at the dose line (workbook v1.2, OPT-STEP-005: "WHY-STEP-01 with a syringe picture marked at
 * the right line"). Inline SVG, drawn from the numbers the API sends: never a number of its own.
 *
 * The barrel is the smallest common U-100 size that holds the dose (30, 50 or 100 units, `scaleUnits`), read from the
 * needle end as a real syringe is: the liquid runs from 0 to the dose, the plunger's stopper sits on the line, and the
 * marked line carries the units. Units are never shown without the concentration beside them (OPT-STEP-006), so the
 * caption names the vial.
 */

export function MicrodoseSyringe({
  units,
  scaleUnits,
  concentration,
  className = '',
  compact = false,
}: {
  units: number;
  scaleUnits: number;
  /** "5": the mg/mL of the vial these units are for. */
  concentration: string;
  className?: string;
  compact?: boolean;
}) {
  const W = 340;
  const H = compact ? 76 : 92;
  const left = 46;
  const right = 268;
  const top = compact ? 26 : 34;
  const barrelH = 30;
  const span = right - left;
  const x = (u: number) => left + (Math.min(Math.max(u, 0), scaleUnits) / scaleUnits) * span;
  const at = x(units);
  const minorEvery = scaleUnits <= 30 ? 0.5 : scaleUnits <= 50 ? 1 : 2;
  const majorEvery = scaleUnits <= 50 ? 5 : 10;
  const ticks: Array<{ u: number; major: boolean; mid: boolean }> = [];
  for (let u = 0; u <= scaleUnits + 1e-9; u += minorEvery) {
    const r = Math.round(u * 2) / 2;
    ticks.push({ u: r, major: Math.abs(r % majorEvery) < 1e-9, mid: Math.abs(r % 1) < 1e-9 && !(Math.abs(r % majorEvery) < 1e-9) });
  }
  const label = `${units} units`;
  const labelW = 14 + label.length * 6.6;
  const labelX = Math.min(Math.max(at - labelW / 2, 4), W - labelW - 4);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={`block h-auto w-full ${className}`}
      role="img"
      aria-label={`U-100 syringe marked at ${units} units, for your ${concentration} mg/mL vial`}
    >
      {/* Needle and hub */}
      <line x1={6} y1={top + barrelH / 2} x2={left - 14} y2={top + barrelH / 2} stroke="var(--ink-muted)" strokeWidth={1.6} strokeLinecap="round" />
      <rect x={left - 15} y={top + barrelH / 2 - 6} width={15} height={12} rx={2} fill="var(--paper-deep)" stroke="var(--hairline)" />
      {/* Barrel */}
      <rect x={left} y={top} width={span} height={barrelH} rx={4} fill="var(--glass-standard-bg)" stroke="var(--navy)" strokeOpacity={0.55} strokeWidth={1.2} />
      {/* The dose: from the needle end to the line */}
      <rect x={left + 1} y={top + 1} width={Math.max(0, at - left - 1)} height={barrelH - 2} rx={3} fill="var(--mint)" />
      {/* Scale */}
      {ticks.map((t) => (
        <line
          key={t.u}
          x1={x(t.u)}
          x2={x(t.u)}
          y1={top + barrelH}
          y2={top + barrelH - (t.major ? 13 : t.mid ? 8 : 5)}
          stroke="var(--navy)"
          strokeOpacity={t.major ? 0.75 : 0.4}
          strokeWidth={t.major ? 1.1 : 0.8}
        />
      ))}
      {ticks
        .filter((t) => t.major)
        .map((t) => (
          <text key={`n${t.u}`} x={x(t.u)} y={top + barrelH + 12} textAnchor="middle" fontSize={9} fill="var(--ink-muted)" style={{ fontFamily: 'inherit' }}>
            {t.u}
          </text>
        ))}
      {/* Plunger: stopper on the line, rod and thumb rest to the right */}
      <rect x={at - 3} y={top + 2} width={6} height={barrelH - 4} rx={1.5} fill="var(--navy)" />
      <rect x={at + 3} y={top + barrelH / 2 - 3} width={Math.max(0, right - at + 26)} height={6} fill="var(--hairline)" />
      <rect x={right + 26} y={top - 4} width={8} height={barrelH + 8} rx={2} fill="var(--hairline)" stroke="var(--navy)" strokeOpacity={0.3} />
      <rect x={right} y={top - 3} width={6} height={barrelH + 6} rx={1.5} fill="var(--paper-deep)" stroke="var(--navy)" strokeOpacity={0.4} />
      {/* The marked line and its label */}
      <line x1={at} x2={at} y1={top - 9} y2={top + barrelH + 2} stroke="var(--forest)" strokeWidth={2} />
      <rect x={labelX} y={top - 26} width={labelW} height={17} rx={8.5} fill="var(--forest)" />
      <text x={labelX + labelW / 2} y={top - 14} textAnchor="middle" fontSize={10.5} fontWeight={600} className="fill-white" style={{ fontFamily: 'inherit' }}>
        {label}
      </text>
      {!compact && (
        <text x={left} y={H - 3} fontSize={9.5} fill="var(--ink-muted)" style={{ fontFamily: 'inherit' }}>
          U-100 syringe, for your {concentration} mg/mL vial
        </text>
      )}
    </svg>
  );
}
